import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuProvider,
  DropdownMenuTrigger,
} from "@shared/ui/DropdownMenu";
import { ErrorMessage, InputLabel } from "@shared/ui/Inputs";
import ChevronDownIcon from "@shared/svg/chevron-down.svg?react";
import React from "react";

export type RadioSelectOption = {
  value: string;
  label: string;
};

type RadioSelectDropdownProps<T extends RadioSelectOption> = {
  keyId: string;
  label?: string;
  placeholder?: string;
  options: T[];
  value: string;
  errorMessage?: string;
  onChange: (value: string) => void;
  /** Custom content for an option row and the trigger (e.g. a type chip). */
  renderLabel?: (option: T) => React.ReactNode;
  /** false: trigger sizes to its content (toolbar switcher) instead of
   * filling the row, and the menu sizes to its options. */
  fullWidth?: boolean;
};

// Same radio affordance as the Emails page account switcher.
const RadioMark = ({ checked }: { checked: boolean }) => (
  <span
    className={`ml-md flex h-md w-md flex-shrink-0 rounded-full border-solid bg-primary ${
      checked ? "border-black border-4" : "border-secondary border"
    }`}
  />
);

const RadioSelectDropdown = <T extends RadioSelectOption>({
  keyId,
  label = "",
  placeholder = "",
  options,
  value,
  errorMessage = "",
  onChange,
  renderLabel,
  fullWidth = true,
}: RadioSelectDropdownProps<T>) => {
  const selected = options.find((option) => option.value === value);

  const renderOptionLabel = (option: T) =>
    renderLabel ? (
      renderLabel(option)
    ) : (
      <span className="min-w-0 truncate">{option.label}</span>
    );

  return (
    <div className="flex flex-col gap-sm">
      {label && (
        <div className="w-fit">
          <InputLabel label={label} keyId={keyId} />
        </div>
      )}
      <DropdownMenuProvider>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            id={keyId}
            className={`${
              fullWidth ? "w-full" : "w-fit max-w-[280px]"
            } h-4xl flex items-center justify-between gap-sm px-md rounded-lg border border-solid cursor-pointer transition-colors aria-expanded:bg-primary aria-expanded:border-brand ${
              errorMessage
                ? "bg-primary border-danger"
                : selected
                ? "bg-primary border-primary text-primary"
                : "bg-accent border-transparent text-secondary"
            }`}
          >
            <span className="flex min-w-0 items-center text-xs font-medium font-manrope">
              {selected ? renderOptionLabel(selected) : placeholder}
            </span>
            <ChevronDownIcon
              width={16}
              height={16}
              className="shrink-0 text-secondary"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={4}
          matchTriggerWidth={fullWidth}
          className={fullWidth ? "" : "min-w-[320px] max-w-[480px]"}
        >
          <div className="max-h-[280px] overflow-y-auto">
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <DropdownMenuItem
                  key={option.value}
                  closeOnSelect
                  // Clicking the selected option clears the field.
                  onSelect={() => onChange(isSelected ? "" : option.value)}
                >
                  <span className="flex min-w-0 flex-1 items-center text-sm">
                    {renderOptionLabel(option)}
                  </span>
                  <RadioMark checked={isSelected} />
                </DropdownMenuItem>
              );
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenuProvider>
      {errorMessage && <ErrorMessage message={errorMessage} />}
    </div>
  );
};

export default RadioSelectDropdown;
